import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useAuthContext } from '../../auth/AuthContext';
import ReportFields from './ReportFields';
import { type Detail, displayDate, failureMessage, request, statusLabels } from './service';

export default function AppDetail() {
  const { appId } = useParams();
  const [params] = useSearchParams();
  const { fetchJWT } = useAuthContext();
  const cache = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const query = useQuery({
    queryKey: ['homebrew', 'app', appId],
    queryFn: async () => {
      const { idToken } = await fetchJWT();
      if (!idToken) throw new Error('Sign in to view this app.');
      return request<Detail>(idToken, `?app=${encodeURIComponent(appId || '')}`);
    },
    retry: false,
  });
  async function save(body: Record<string, unknown>, form?: HTMLFormElement) {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const { idToken } = await fetchJWT();
      if (!idToken) throw new Error('Sign in to update this app.');
      await request(idToken, '', { ...body, app: appId });
      form?.reset();
      setMessage(
        body.action === 'note'
          ? 'Note added.'
          : body.needed
            ? 'Added to My Apps.'
            : 'Removed from My Apps.',
      );
      await cache.invalidateQueries({ queryKey: ['homebrew'] });
    } catch (err) {
      setError(failureMessage(err));
    } finally {
      setBusy(false);
    }
  }
  function note(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    void save(
      { ...Object.fromEntries(new FormData(event.currentTarget)), action: 'note' },
      event.currentTarget,
    );
  }
  const data = query.data;
  return (
    <>
      <Link className="govuk-back-link" to="/homebrew/library">
        App Library
      </Link>
      {query.isPending && (
        <p className="govuk-body" role="status">
          Loading app...
        </p>
      )}
      {(query.error || error) && (
        <p className="govuk-error-message" role="alert">
          {error || failureMessage(query.error)}
        </p>
      )}
      {query.error && (
        <button
          className="govuk-button govuk-button--secondary"
          type="button"
          onClick={() => void query.refetch()}
        >
          Try again
        </button>
      )}
      {message && (
        <p className="govuk-body" role="status">
          {message}
        </p>
      )}
      {data && (
        <>
          <h1 className="govuk-heading-xl">{data.app.name}</h1>
          {params.has('existing') && (
            <p className="govuk-inset-text">
              This app is already in the library. Your new entry was not saved. Add your need or
              note here.
            </p>
          )}
          <p className="govuk-body-l">{data.app.description}</p>
          <h2 className="govuk-heading-m">Why it is needed</h2>
          <p className="govuk-body homebrew-note-text">{data.app.purpose}</p>
          <p className="govuk-body-s">
            Added by {data.app.created_by} · {displayDate(data.app.created_at)}
          </p>
          <div className="govuk-inset-text">
            <p className="govuk-body">
              <strong>
                {data.app.votes} {data.app.votes === 1 ? 'person needs' : 'people need'} this app
              </strong>
              . Voters are not listed.
            </p>
            <button
              className="govuk-button govuk-button--secondary"
              type="button"
              disabled={busy}
              onClick={() => void save({ action: 'need', needed: !data.app.needed })}
            >
              {data.app.needed ? 'I no longer need this' : 'I need this too'}
            </button>
          </div>
          <h2 className="govuk-heading-m">Homebrew availability</h2>
          {data.catalogue_error && (
            <p className="govuk-body" role="alert">
              {data.catalogue_error}
            </p>
          )}
          {!data.package && !data.catalogue_error && (
            <p className="govuk-body">
              No Homebrew package linked. This records a software gap, not proof that the app cannot
              run on a Mac. Search Homebrew for alternatives and share findings in the notes.
            </p>
          )}
          {data.package && (
            <>
              <p className="govuk-body">
                Status: <strong>{data.package.availability}</strong>
                {data.package.reason && ` (${data.package.reason})`}. Version:{' '}
                {data.package.version || 'not specified'}.
              </p>
              <p className="govuk-body">
                <a
                  className="govuk-link"
                  href={data.package.source}
                  target="_blank"
                  rel="noreferrer"
                >
                  View Homebrew requirements and package details (opens in new tab)
                </a>
              </p>
              {data.package.command && (
                <>
                  <p className="govuk-body">
                    With Homebrew already set up, open Terminal, paste this command and press
                    Return. Follow your organisation’s software rules.
                  </p>
                  <div className="homebrew-command">
                    <code>{data.package.command}</code>
                    <button
                      className="govuk-button govuk-button--secondary"
                      type="button"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(data.package?.command || '');
                          setMessage('Install command copied.');
                        } catch {
                          setError('Unable to copy. Select and copy the command above.');
                        }
                      }}
                    >
                      Copy command
                    </button>
                  </div>
                </>
              )}
              {data.package.availability === 'disabled' && (
                <p className="govuk-body">
                  Homebrew has disabled this package. No install command is offered.
                </p>
              )}
            </>
          )}
          <h2 className="govuk-heading-m">Reports from MacBook users</h2>
          <p className="govuk-body">
            Latest working-status report per person. Results may differ by app version, MacBook and
            device restrictions.
          </p>
          <ul className="govuk-list govuk-list--bullet">
            <li>Works: {data.reports.works}</li>
            <li>Works with issues: {data.reports.issues}</li>
            <li>Does not work: {data.reports.does_not_work}</li>
          </ul>
          {Object.values(data.reports).every((count) => count === 0) && (
            <p className="govuk-body">No working-status reports yet.</p>
          )}
          <h2 className="govuk-heading-m">Notes</h2>
          <p className="govuk-body">
            Newest first. Notes include the author’s email and time posted.
          </p>
          {data.notes.length === 0 && <p className="govuk-body">No notes yet.</p>}
          <ol className="govuk-list homebrew-app-list">
            {data.notes.map((item) => (
              <li key={item.id}>
                <p className="govuk-body">
                  <strong>{item.email}</strong> ·{' '}
                  <time dateTime={item.created_at}>{displayDate(item.created_at)}</time>
                </p>
                <p className="govuk-body-s">
                  {statusLabels[item.result]}
                  {item.app_version && ` · App ${item.app_version}`}
                  {item.macos_version && ` · macOS ${item.macos_version}`}
                  {item.processor && ` · ${item.processor}`}
                </p>
                <p className="govuk-body homebrew-note-text">{item.text}</p>
              </li>
            ))}
          </ol>
          <h2 className="govuk-heading-m">Add a note or report</h2>
          <form onSubmit={note}>
            <ReportFields />
            <button className="govuk-button" type="submit" disabled={busy}>
              Add note
            </button>
          </form>
        </>
      )}
    </>
  );
}
